import type { TError, TSubmitQuizAttempt } from '@onesource/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';
import type { TQuizAttempt } from './start-attempt';

/**
 * Quiz Answer with grading result
 */
export interface TQuizAnswer {
  id: string;
  attemptId: string;
  questionId: string;
  selectedOptionIds: string[];
  orderingSequence: string[];
  textAnswer: string | null;
  isCorrect: boolean | null;
  pointsAwarded: number;
  gradedAt: string | null;
  feedback: string | null;
}

/**
 * Graded attempt response
 */
export interface TGradedAttempt extends TQuizAttempt {
  answers: TQuizAnswer[];
}

/**
 * Submit attempt input
 */
interface SubmitAttemptInput {
  attemptId: string;
  data: TSubmitQuizAttempt;
}

/**
 * Hook to submit a quiz attempt for grading
 */
export const useSubmitAttempt = () => {
  const queryClient = useQueryClient();

  return useMutation<TGradedAttempt, TError, SubmitAttemptInput>({
    mutationFn: async ({ attemptId, data }) => {
      const res = await axios.post<TGradedAttempt>(`/community-hub/attempts/${attemptId}/submit`, data);
      return res.data;
    },
    onSuccess: (result, { attemptId }) => {
      // Cache the graded result
      queryClient.setQueryData([ENTITY, 'attempt', attemptId], result);
      // Invalidate user attempts list
      queryClient.invalidateQueries({ queryKey: [ENTITY, 'quiz', result.quizId, 'attempts'] });
    },
  });
};
